import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { publicAPI } from '../../services/api'
import LoadingSpinner from '../../components/LoadingSpinner'
import ErrorMessage from '../../components/ErrorMessage'
import { useTheme } from '../../contexts/ThemeContext'
import { localizeApiDetail } from '../../i18n/apiError'
import { useLatest } from '../Bruktbil/useLatest'
import type { AkseldriftAlder as Alder, AkseldriftQuery, ModelRow, Periods } from './types'

// Same colours as the Oversikt chart
const COLORS = { '2wd': '#0e7490', '4wd': '#ea580c' }

const int = (n: number) => n.toLocaleString('nb-NO')
const pct = (n: number | null) =>
  n === null ? '–' : `${n.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`

/** Teknisk › Akseldrift › Alder: owner age groups × akseldrift, optionally for one brand or model. */
export default function AkseldriftAlder({ queryKey, onPeriods }: {
  queryKey: string
  /** Reports the periods of the latest answer, for the explanation box */
  onPeriods: (periods: Periods) => void
}) {
  const { t } = useTranslation('teknisk')
  const { isDark } = useTheme()
  const [models, setModels] = useState<ModelRow[]>([])
  const [merke, setMerke] = useState('')
  const [modelId, setModelId] = useState('')
  const [alder, setAlder] = useState<Alder | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // The brand and model choices: models sold in the selection (cached on the server)
  useLatest(alive => {
    publicAPI.getAkseldriftModeller(JSON.parse(queryKey))
      .then(res => { if (alive()) setModels(res.data.data) })
      .catch(() => { if (alive()) setModels([]) })
  }, [queryKey])

  useLatest(alive => {
    const params: AkseldriftQuery & { merke?: string; master_model_id?: number } = JSON.parse(queryKey)
    if (modelId) params.master_model_id = Number(modelId)
    else if (merke) params.merke = merke
    setLoading(true)
    setError(null)
    publicAPI.getAkseldriftAlder(params)
      .then(res => {
        if (!alive()) return
        setAlder(res.data)
        onPeriods(res.data.periods)
      })
      .catch(err => {
        if (alive()) setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('akseldrift.loadError'))
      })
      .finally(() => { if (alive()) setLoading(false) })
  }, [queryKey, merke, modelId])

  const brands = useMemo(() => [...new Set(models.map(m => m.brand))].sort((a, b) => a.localeCompare(b, 'nb')), [models])
  const brandModels = useMemo(
    () => models.filter(m => m.brand === merke).sort((a, b) => a.model.localeCompare(b.model, 'nb')),
    [models, merke],
  )

  const drive2 = t('akseldrift.drive.2wd')
  const drive4 = t('akseldrift.drive.4wd')
  const axisColor = isDark ? '#94a3b8' : '#64748b'
  const gridColor = isDark ? '#334155' : '#e2e8f0'

  return (
    <div className="space-y-6" data-testid="akseldrift-alder">
      <div className="card p-4 flex flex-wrap items-end gap-4">
        <div>
          <label htmlFor="teknisk-alder-merke" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('akseldrift.alder.brand')}
          </label>
          <select
            id="teknisk-alder-merke"
            value={merke}
            onChange={e => { setMerke(e.target.value); setModelId('') }}
            className="input-field"
          >
            <option value="">{t('akseldrift.alder.allBrands')}</option>
            {brands.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="teknisk-alder-modell" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('akseldrift.alder.model')}
          </label>
          <select
            id="teknisk-alder-modell"
            value={modelId}
            onChange={e => setModelId(e.target.value)}
            disabled={!merke}
            className="input-field disabled:opacity-50"
          >
            <option value="">{t('akseldrift.alder.allModels')}</option>
            {brandModels.map(m => <option key={m.master_model_id} value={m.master_model_id}>{m.model}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorMessage message={error} />
      ) : alder && (
        <>
          <div className="card p-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">{t('akseldrift.alder.chartTitle')}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('akseldrift.alder.chartHint')}</p>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={alder.groups} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                  <XAxis dataKey="age_group" stroke={axisColor} fontSize={12} />
                  <YAxis tickFormatter={int} stroke={axisColor} fontSize={12} width={56} />
                  <Tooltip formatter={(v: number) => int(v)} />
                  <Legend />
                  <Bar dataKey="count_2wd" name={drive2} fill={COLORS['2wd']} />
                  <Bar dataKey="count_4wd" name={drive4} fill={COLORS['4wd']} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="card p-4">
            <div className="overflow-x-auto">
              <table className="data-table data-table-compact" data-testid="akseldrift-alder-table">
                <thead>
                  <tr>
                    <th>{t('akseldrift.alder.ageGroup')}</th>
                    <th className="text-right">{t('akseldrift.table.count2wd')}</th>
                    <th className="text-right">{t('akseldrift.table.count4wd')}</th>
                    <th className="text-right">{t('akseldrift.alder.dist', { drive: drive2 })}</th>
                    <th className="text-right">{t('akseldrift.alder.dist', { drive: drive4 })}</th>
                    <th className="text-right">{t('akseldrift.alder.share2wdInGroup')}</th>
                  </tr>
                </thead>
                <tbody>
                  {alder.groups.map(g => (
                    <tr key={g.age_group}>
                      <td>{g.age_group}</td>
                      <td className="text-right">{int(g.count_2wd)}</td>
                      <td className="text-right">{int(g.count_4wd)}</td>
                      <td className="text-right">{pct(g.dist_2wd)}</td>
                      <td className="text-right">{pct(g.dist_4wd)}</td>
                      <td className="text-right">{pct(g.share_2wd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2" data-testid="akseldrift-alder-note">
              {t('akseldrift.alder.withoutAge', { count: int(alder.without_age) })}
            </p>
          </div>
        </>
      )}
    </div>
  )
}
